// ------------------------------------------------------------------
// 新TOPページ：SOLUTION（課題から選ぶ）タブ
// docs/TOPページデザイン/new_amazing/js/main.js の initSolutionTabs を移植。
// タブを押すと、その段の直下のパネル群に課題一覧が開く（同じタブをもう一度押すと閉じる）。
// 1段のタブ数は PC 3つ・スマホ（768px 以下）2つ。Liquid は3つずつ出力し、スマホでは JS で段を組み直す。
// ------------------------------------------------------------------
if (!customElements.get('ntop-solution')) {
  class NtopSolution extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-solution-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-solution-panel]'));
      this.panelGroups = Array.from(this.querySelectorAll('.ntop-solution-panels'));
      this.layout = this.querySelector('.ntop-solution-layout');
      if (!this.tabs.length) return;

      this.mobileQuery = window.matchMedia('(max-width: 768px)');
      this.onBreakpoint = () => this.regroup(this.mobileQuery.matches ? 2 : 3);
      this.mobileQuery.addEventListener?.('change', this.onBreakpoint);
      this.onBreakpoint();

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const key = tab.getAttribute('data-solution-tab');
          this.classList.remove('is-restoring');
          this.open(tab.classList.contains('is-active') ? null : key);
          this.saveState();
        });
      });

      // 課題（個別ページへのリンク）を押したら、開いているタブとスクロール位置を
      // この履歴エントリに覚えさせる（戻ったときに restore() で復元する）
      this.addEventListener('click', (e) => {
        if (!e.target.closest?.('.ntop-challenge-card[href]')) return;
        this.saveState();
      });
      this.restore();

      // テーマエディタでタブ（カテゴリ）ブロックを選択したら、そのパネルを開く
      this.onBlockSelect = (e) => {
        const key = e.target?.getAttribute?.('data-solution-tab');
        if (key) this.open(key);
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      this.onResize = () => this.updatePanelGroups();
      window.addEventListener('resize', this.onResize);
      this.updatePanelGroups();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.mobileQuery?.removeEventListener?.('change', this.onBreakpoint);
    }

    // タブとパネルを rowSize 個ずつの段に組み直す（各段＝タブ群＋その直下のパネル群）。
    // 要素は移動するだけなので、クリック処理や開いている状態はそのまま残る
    regroup(rowSize) {
      if (!this.layout || this.rowSize === rowSize) return;
      const panelsByKey = new Map(this.panels.map((p) => [p.getAttribute('data-solution-panel'), p]));
      const rowCount = Math.ceil(this.tabs.length / rowSize);
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < rowCount; i++) {
        const tabGroup = document.createElement('div');
        tabGroup.className = 'ntop-solution-tabs';
        const panelGroup = document.createElement('div');
        panelGroup.className = 'ntop-solution-panels';
        if (i === rowCount - 1 && rowCount > 1) panelGroup.classList.add('ntop-solution-panels--bottom');
        this.tabs.slice(i * rowSize, (i + 1) * rowSize).forEach((tab) => {
          tabGroup.appendChild(tab);
          const panel = panelsByKey.get(tab.getAttribute('data-solution-tab'));
          if (panel) panelGroup.appendChild(panel);
        });
        fragment.append(tabGroup, panelGroup);
      }
      this.layout.replaceChildren(fragment);
      this.panelGroups = Array.from(this.layout.querySelectorAll('.ntop-solution-panels'));
      this.rowSize = rowSize;
      this.updatePanelGroups();
    }

    open(key) {
      this.tabs.forEach((t) => {
        const active = key !== null && t.getAttribute('data-solution-tab') === key;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-expanded', active ? 'true' : 'false');
      });
      this.panels.forEach((p) => {
        p.classList.toggle('is-active', key !== null && p.getAttribute('data-solution-panel') === key);
      });
      this.updatePanelGroups();
    }

    saveState() {
      const active = this.tabs.find((t) => t.classList.contains('is-active'));
      try {
        history.replaceState(
          {
            ...(history.state || {}),
            ntopSolutionTab: active ? active.getAttribute('data-solution-tab') : null,
            ntopSolutionScrollY: window.scrollY
          },
          ''
        );
      } catch (_err) {
        // 履歴を書き換えられない環境では復元しない
      }
    }

    // 「戻る」でページが読み込み直されたとき（スマホなど、ブラウザのキャッシュから
    // 復元されない場合）に、閲覧していた課題のパネルを開き直して元の位置へ戻す
    restore() {
      const state = history.state;
      const key = state?.ntopSolutionTab;
      if (!key || !this.tabs.some((t) => t.getAttribute('data-solution-tab') === key)) return;
      const nav = performance.getEntriesByType?.('navigation')[0];
      if (nav && nav.type !== 'back_forward') return;

      this.classList.add('is-restoring');
      this.open(key);

      const y = state.ntopSolutionScrollY;
      if (typeof y !== 'number') return;
      // ブラウザの位置復元が、パネルを開く前の高さで行われてもずれないよう、
      // 読み込みの区切りごとに覚えておいた位置へ合わせ直す（ユーザーが動かしたらやめる）
      let userScrolled = false;
      const stop = () => {
        userScrolled = true;
      };
      ['wheel', 'touchstart', 'keydown'].forEach((type) =>
        window.addEventListener(type, stop, { once: true, passive: true })
      );
      const align = () => {
        if (!userScrolled && Math.abs(window.scrollY - y) > 2) window.scrollTo(0, y);
      };
      requestAnimationFrame(align);
      if (document.readyState === 'complete') setTimeout(align, 0);
      else window.addEventListener('load', () => setTimeout(align, 0), { once: true });
    }

    updatePanelGroups() {
      this.panelGroups.forEach((group) => {
        const activePanel = group.querySelector('.ntop-solution-panel.is-active');
        group.classList.toggle('has-active-panel', Boolean(activePanel));
        group.style.setProperty('--solution-panel-height', activePanel ? `${activePanel.scrollHeight}px` : '0px');
      });
    }
  }

  customElements.define('ntop-solution', NtopSolution);
}
